import { InvoiceTimecard } from 'types/invoice-details';
import {
    InvoiceAmountDetailsTotalsDto,
    InvoiceData,
    InvoiceDetailsTotals,
    InvoiceMatterDetailsDto,
    InvoiceTotals,
} from 'types/invoices';
import { PricingLimitationType } from 'types/matters';

/**
 * How a case reads in the summary. An hourly case bills its timecards, so the billed table lists them by person; a
 * fixed-fee, monthly-fee or copied case bills its fee rows, and its timecards are listed apart, as not in the total.
 */
export type SummaryLayout = 'hourly' | 'fixed' | 'monthly' | 'copy';

export interface SummaryPerson {
    id: number;
    name: string;
    hours: number;
    /** What the invoice bills for the person's timecards: after its discounts and its cap. */
    amount: number;
    /** The same timecards at their price-list rates. */
    value: number;
}

export type RowCaption =
    | { type: 'other-fee' }
    | { type: 'recurring'; interval?: string | null }
    | { type: 'row-type'; code: string };

export type SummaryLine =
    /** `expenseTypes`: the expenses' `EXPENSE_TYPE` codes, each once, in the totals' order. */
    | { kind: 'expenses'; key: string; expenseTypes: string[]; amount: number }
    | { kind: 'row'; key: string; description?: string; caption: RowCaption; amount: number };

/** A case's cap cutting this invoice, from its fee log: the fees billed, their value at rates and the cut. */
export interface CapCut {
    billed: number;
    value: number;
    cut: number;
    /** The figures' currency: the payment currency where a read holds them in it, else the invoice's own. */
    currency?: string;
}

export interface SummaryCase {
    matterId: number;
    fullName: string;
    /** The case's fee arrangement; on a shared-payment copy, the one it had before the split. */
    arrangement: string;
    layout: SummaryLayout;
    /** Largest first: by the net amount on an hourly case, by the value at rates on any other. */
    people: SummaryPerson[];
    hours: number;
    /** An hourly case's Fees row: the figure its totals bill, so the billed table adds up to the invoice total. */
    fees: number;
    value: number;
    lines: SummaryLine[];
    capCut: CapCut | null;
}

type AmountTotals = InvoiceAmountDetailsTotalsDto['invoice'];

export interface InvoiceSummaryTables {
    currency?: string;
    cases: SummaryCase[];
    /** The cases whose timecards the second table lists, as not in the total. */
    timecardCases: SummaryCase[];
    timecardsTitle: 'fixed' | 'monthly' | 'copy';
    notice: { type: 'copy' | 'shared'; position: number; count: number } | null;
    /** The billed table's total: the whole invoice's, on a shared-payment copy too. */
    total?: AmountTotals;
    /** A shared-payment copy's rate and its own totals, which its receipt shows as the share. */
    share: { rate: number; total?: AmountTotals } | null;
}

export interface InvoiceSummaryInput {
    invoice: Pick<
        InvoiceData,
        | 'id'
        | 'isSplit'
        | 'invoiceCopies'
        | 'invoiceMatters'
        | 'paymentShareRate'
        | 'preSplitTotals'
        | 'sharedPaymentGroup'
    >;
    /** A shared-payment copy, read as its receipt reads it: the whole invoice, and this payer's share beside it. */
    isShared: boolean;
    totals?: InvoiceTotals;
    timecards?: InvoiceTimecard[];
}

const sum = <T>(items: T[], of: (item: T) => number) => items.reduce((total, item) => total + of(item), 0);

// The payment currency's figure; where the invoice is in the payment currency the twin may be absent, and the
// invoice currency's figure is the same amount.
const paid = (item: { totalWoVat?: number; totalWoVatPaymentCurrency?: number }) =>
    item.totalWoVatPaymentCurrency ?? item.totalWoVat ?? 0;

/** Not fixed reads as hourly: pro bono and success-fee cases invoiced by hand bill their timecards as hourly ones do. */
export const layoutOf = (arrangement: string, isCopy: boolean): SummaryLayout => {
    if (isCopy) {
        return 'copy';
    }
    if (arrangement === PricingLimitationType.FIXED_ONETIME) {
        return 'fixed';
    }
    return arrangement === PricingLimitationType.FIXED_RECURRING ? 'monthly' : 'hourly';
};

/** The timecards the invoice's timecard totals sum: unedited, billable and not removed. */
export const isBilledTimecard = (timecard: InvoiceTimecard) =>
    !timecard.editIntentTypeCode && !timecard.isNonBillable && !timecard.isRemoved;

function peopleOf(timecards: InvoiceTimecard[], matterId: number, layout: SummaryLayout): SummaryPerson[] {
    const byPerson = new Map<number, SummaryPerson>();
    for (const timecard of timecards) {
        if (timecard.matter?.id !== matterId || !isBilledTimecard(timecard)) {
            continue;
        }
        const { id, name } = timecard.employee;
        const person = byPerson.get(id) ?? { id, name, hours: 0, amount: 0, value: 0 };
        const log = timecard.calculationLog?.paymentCurrency ?? timecard.calculationLog;
        person.hours += (timecard.billedTimeInSeconds ?? 0) / 3600;
        person.amount += log?.finalAmount ?? 0;
        person.value += timecard.totalBeforeAdjustmentInPaymentCurrency ?? timecard.totalBeforeAdjustment ?? 0;
        byPerson.set(id, person);
    }
    const largest = layout === 'hourly' ? (p: SummaryPerson) => p.amount : (p: SummaryPerson) => p.value;
    return [...byPerson.values()].sort((a, b) => largest(b) - largest(a));
}

/**
 * The cut is the case's own: a discount is no cut, so a capped case under its cap gets no notice. Its figures are in
 * the payment currency where a read holds them in it: the fee log's payment twin once the invoice is shown in that
 * currency, the invoice currency's figures when the two are one. Otherwise no read converts them, so they stay in the
 * invoice's own currency, named on every amount: the cut is still stated, and no rate is made up for it.
 */
function capCutOf(link: InvoiceMatterDetailsDto | undefined, totals: InvoiceTotals | undefined): CapCut | null {
    const log = link?.calculationLog;
    const twin = log?.paymentCurrency?.fees;
    const isPaid = twin?.matterCashAdjustment !== undefined;
    const fees = isPaid ? twin : log?.fees;
    return (log?.fees?.matterCashAdjustment ?? 0) < 0 && fees
        ? {
              billed: fees.totalAfterMatterAdjustments ?? 0,
              value: fees.totalBeforeMatterAdjustments ?? 0,
              cut: -(fees.matterCashAdjustment ?? 0),
              currency: (isPaid ? totals?.paymentCurrency : totals?.invoiceCurrency)?.currencyTypeCode,
          }
        : null;
}

function caseOf(totals: InvoiceDetailsTotals, input: InvoiceSummaryInput, isCopy: boolean): SummaryCase {
    const { invoice, isShared, totals: invoiceTotals } = input;
    const link = invoice.invoiceMatters?.find((matter) => matter.matterId === totals.matterId);
    const arrangement = (isShared ? link?.preSplitFeeArrangementTypeCode : null) ?? totals.feeArrangementTypeCode;
    const layout = layoutOf(arrangement, isCopy);
    const isHourly = layout === 'hourly';
    const people = peopleOf(input.timecards ?? [], totals.matterId, layout);
    const rows = (totals.invoiceRowTotals ?? []).filter(
        (row) => !row.isRemoved && row.invoiceRowTypeCode !== 'GROUPING'
    );
    const adminRows = rows.filter((row) => row.invoiceRowTypeCode === 'ADMIN_FEE');
    const feeRows = rows.filter((row) => row.invoiceRowTypeCode !== 'ADMIN_FEE');
    // A shared-payment split bills an hourly case's timecards as FIXED_FEE rows: those are its Fees, not listed again.
    // shortcut: the totals do not tell the split's rows from a FIXED_FEE row added by hand before it, which reads as
    // part of the Fees here, or as the recurring fee on a monthly case; upgrade once the rows carry their pre-split type.
    const splitFees = isShared && isHourly ? feeRows.filter((row) => row.invoiceRowTypeCode === 'FIXED_FEE') : [];
    const isRecurringRow = (code: string) =>
        layout === 'monthly' && (code === 'MONTHLY_FEE' || (isShared && code === 'FIXED_FEE'));
    const captionOf = (code: string): RowCaption => {
        if (isHourly && code === 'FIXED_FEE') {
            return { type: 'other-fee' };
        }
        return isRecurringRow(code)
            ? {
                  type: 'recurring',
                  interval: totals.matterBillingSummary?.recurringFeeDetails?.invoicingIntervalTypeCode,
              }
            : { type: 'row-type', code };
    };
    const rowLines = (list: typeof rows): SummaryLine[] =>
        list.map((row) => ({
            kind: 'row',
            key: `row-${row.invoiceRowId}`,
            description: row.description,
            caption: captionOf(row.invoiceRowTypeCode),
            amount: paid(row),
        }));
    const expenses = totals.invoiceExpenseTotals ?? [];
    const expenseTypes = new Set(expenses.flatMap((expense) => (expense.expenseTypes ?? '').split(', ')));
    expenseTypes.delete('');
    const expenseLines: SummaryLine[] = expenses.length
        ? [
              {
                  kind: 'expenses',
                  key: `expenses-${totals.matterId}`,
                  expenseTypes: [...expenseTypes],
                  amount: sum(expenses, paid),
              },
          ]
        : [];
    const otherRows = rowLines(feeRows.filter((row) => !splitFees.includes(row)));
    return {
        matterId: totals.matterId,
        fullName: totals.fullName,
        arrangement,
        layout,
        people,
        hours: sum(people, (person) => person.hours),
        fees: isHourly ? sum(totals.invoiceTimecardTotals ?? [], paid) + sum(splitFees, paid) : 0,
        value: sum(people, (person) => person.value),
        // The fee first: the timecards' Fees row on an hourly case, its fee rows on any other. Then the expenses on
        // one line, then every other row, the admin fee last.
        lines: isHourly
            ? [...expenseLines, ...otherRows, ...rowLines(adminRows)]
            : [...otherRows, ...expenseLines, ...rowLines(adminRows)],
        capCut: isHourly ? capCutOf(link, invoiceTotals) : null,
    };
}

/**
 * What the invoice bills, case by case, in the payment currency, down to the invoice total: the dialog's tables and
 * its notices. A shared-payment copy's position is its group's, the one the record header shows; a plain copy's counts
 * the invoice copied from first, in the order the copies were made.
 */
export function buildInvoiceSummary(input: InvoiceSummaryInput): InvoiceSummaryTables {
    const { invoice, isShared, totals } = input;
    const isCopy = !!invoice.isSplit && !isShared;
    const cases = (totals?.invoiceDetailsTotals ?? []).map((matter) => caseOf(matter, input, isCopy));
    const timecardCases = cases.filter((matter) => matter.layout !== 'hourly' && matter.people.length > 0);
    const copyIds = [...new Set([...(invoice.invoiceCopies ?? []).map((copy) => copy.id), invoice.id])].sort(
        (a, b) => a - b
    );
    const group = invoice.sharedPaymentGroup;
    const allMonthly = timecardCases.every((matter) => matter.layout === 'monthly');
    return {
        currency: totals?.paymentCurrency.currencyTypeCode,
        cases,
        timecardCases,
        timecardsTitle: isCopy ? 'copy' : allMonthly ? 'monthly' : 'fixed',
        notice: isShared
            ? { type: 'shared', position: group?.position ?? 1, count: group?.invoiceCount ?? 1 }
            : isCopy
              ? { type: 'copy', position: copyIds.indexOf(invoice.id) + 1, count: copyIds.length }
              : null,
        total: (isShared ? invoice.preSplitTotals : totals)?.paymentCurrency.invoice,
        share: isShared
            ? { rate: Number(invoice.paymentShareRate ?? 0), total: totals?.paymentCurrency.invoice }
            : null,
    };
}
